import PropTypes from "prop-types";

import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import MDTypography from "components/MDTypography";
import MediaPicker from "components/content/MediaPicker";
import MetricsEditor from "components/content/MetricsEditor";

const SectionLabel = ({ children }) => (
  <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
    {children}
  </MDTypography>
);
SectionLabel.propTypes = { children: PropTypes.node.isRequired };

/**
 * Batch 3.1 — <CaseStudyFields>. The Case Study type block. Challenge /
 * Solution / Results are required plain-text sections (the backend does not
 * sanitize them, so they stay textareas, not rich text).
 */
function CaseStudyFields({ value, onChange, websiteId }) {
  const set = (key) => (event) => onChange({ [key]: event.target.value });

  return (
    <Stack spacing={3}>
      <SectionLabel>Case study details</SectionLabel>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label="Client name"
          value={value.client_name}
          onChange={set("client_name")}
          fullWidth
          required
        />
        <TextField
          label="Client company"
          value={value.client_company}
          onChange={set("client_company")}
          fullWidth
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField label="Industry" value={value.industry} onChange={set("industry")} fullWidth />
        <TextField
          label="Project duration"
          value={value.project_duration}
          onChange={set("project_duration")}
          placeholder="e.g. 6 months"
          fullWidth
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label="Project start date"
          type="date"
          value={value.project_start_date}
          onChange={set("project_start_date")}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Project end date"
          type="date"
          value={value.project_end_date}
          onChange={set("project_end_date")}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <Divider />
      <SectionLabel>Narrative</SectionLabel>

      <TextField
        label="Challenge"
        value={value.challenge}
        onChange={set("challenge")}
        fullWidth
        multiline
        minRows={3}
        required
      />
      <TextField
        label="Solution"
        value={value.solution}
        onChange={set("solution")}
        fullWidth
        multiline
        minRows={3}
        required
      />
      <TextField
        label="Results"
        value={value.results}
        onChange={set("results")}
        fullWidth
        multiline
        minRows={3}
        required
      />

      <Divider />
      <MetricsEditor value={value.metrics} onChange={(metrics) => onChange({ metrics })} />

      <TextField
        label="Testimonial quote"
        value={value.testimonial_quote}
        onChange={set("testimonial_quote")}
        fullWidth
        multiline
        minRows={2}
      />

      <MediaPicker
        label="Client logo"
        value={value.client_logo}
        onChange={(id) => onChange({ client_logo: id })}
        websiteId={websiteId}
        visibility="public"
        accept="image/*"
      />
    </Stack>
  );
}

CaseStudyFields.defaultProps = {
  websiteId: undefined,
  context: {},
};

CaseStudyFields.propTypes = {
  value: PropTypes.shape({
    client_name: PropTypes.string,
    client_company: PropTypes.string,
    industry: PropTypes.string,
    project_duration: PropTypes.string,
    project_start_date: PropTypes.string,
    project_end_date: PropTypes.string,
    challenge: PropTypes.string,
    solution: PropTypes.string,
    results: PropTypes.string,
    metrics: PropTypes.array,
    testimonial_quote: PropTypes.string,
    client_logo: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  websiteId: PropTypes.string,
  context: PropTypes.object,
};

export default CaseStudyFields;
